import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCamera } from '@pella/barcode-scanner';
import { useCurrentUser } from '@pella/react-current-user';
import { useAppContext } from '../../../providers';
import { useRequests, useResourceQueuesByStatus, useSocket } from '../../../hooks';
import { searchMultiFields, getActiveQueueIds, normalizeToLower, fnv1aHash } from '../../../utils';

import { Button, Icon, Modal, ModalContent, ModalFooter, ModalHeader, ModalRoot } from '@pella/sash-framework';
import { SearchInput } from '../../atoms';
import { StatusAccordion } from '../../molecules';

import { QUERY_KEYS, NEW_COMMUNICATION_MESSAGE } from '../../../data/constants';
import { REQUESTS, QUEUES } from './config';

import * as classes from './StatusContent.module.css';

const TABS = { WORKLIST: 'Worklist', QUEUES: 'Queues' };

/**
 * Builds a stable signature for communications the current user has not read.
 * @param {{ available?: Local.RequestWithCommunication[], accepted?: Local.RequestWithCommunication[], onhold?: Local.RequestWithCommunication[] } | undefined} requests - Request groups shown in status accordions.
 * @param {string | null | undefined} userName - Current user's identifier.
 * @returns {string} A signature representing unread communications, or an empty string when none exist.
 */
const getUnreadCommunicationSignature = (requests, userName) => {
  const normalizedUserName = normalizeToLower(userName);

  if (!requests || !normalizedUserName) {
    return '';
  }

  const unreadCommunications = [requests.available, requests.accepted, requests.onhold]
    .flatMap((requestGroup) => requestGroup ?? [])
    .flatMap((request) =>
      (request.communications ?? [])
        .filter((entry) => {
          const readByCurrentUser = (entry.readBy ?? []).some(
            (reader) => normalizeToLower(reader) === normalizedUserName,
          );

          return !readByCurrentUser;
        })
        .map((entry) => `${request.id}:${entry.timeStamp}:${entry.userName}:${entry.communication}`),
    );

  if (!unreadCommunications.length) {
    return '';
  }
  return fnv1aHash(unreadCommunications.toSorted().join('|'));
};

/**
 * StatusContent organism
 */
export const StatusContent = () => {
  const queryClient = useQueryClient();
  const { pathname } = useLocation();
  const { userid } = useCurrentUser();

  const [searchValue, setSearchValue] = useState('');
  const [dismissedUnreadSignature, setDismissedUnreadSignature] = useState('');
  const { activeTab, selectedPlant, selectedResource, selectedLocation } = useAppContext();
  const { startCamera } = useCamera();

  const handleBarcodeScan = () => {
    startCamera(
      (result) => setSearchValue(result),
      () => {
        // Camera dismissed — no action needed, user remains on the worklist
      },
    );
  };

  const useRequestsData = pathname === '/request' || activeTab === TABS.WORKLIST;

  const { socketMessage } = useSocket({
    event: `${selectedPlant}-request`,
    enabled: useRequestsData,
  });

  // Request data
  const { data: requests } = useRequests(pathname === '/worklist' ? 'resource' : 'location', useRequestsData);
  const acceptedRequests = searchMultiFields(searchValue, requests?.accepted);
  const availableRequests = searchMultiFields(searchValue, requests?.available);
  const onHoldRequests = searchMultiFields(searchValue, requests?.onhold);
  const unreadCommunicationSignature = getUnreadCommunicationSignature(requests, userid);
  const showNewCommunicationModal =
    useRequestsData &&
    Boolean(unreadCommunicationSignature) &&
    unreadCommunicationSignature !== dismissedUnreadSignature;

  // Resource Queue data
  const { data: queues } = useResourceQueuesByStatus(pathname === '/worklist');
  const permanentQueues = queues?.permanent?.length ? queues.permanent : null;
  const temporaryQueues = queues?.temporary?.length ? queues.temporary : null;
  const availableQueues = queues?.available?.length ? queues.available : null;

  // Invalidate requests queries on relevant socket messages
  useEffect(() => {
    if (socketMessage) {
      let validMessage;

      if (pathname === '/worklist') {
        const activeQueueIds = getActiveQueueIds(permanentQueues, temporaryQueues);
        const queueId = socketMessage?.action?.queue?.id;
        validMessage = queueId !== undefined && activeQueueIds.includes(queueId);
        /* v8 ignore start */
      } else if (pathname === '/request') {
        validMessage = socketMessage?.action?.location?.id === selectedLocation;
      }
      /* v8 ignore stop */

      /* v8 ignore next */
      if (validMessage) {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REQUESTS_BY_RESOURCE, selectedResource] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REQUESTS_BY_LOCATION, selectedLocation] });
      }
    }
  }, [socketMessage, permanentQueues, temporaryQueues, queryClient, selectedResource, selectedLocation, pathname]);

  const handleCloseNewCommunicationModal = () => {
    setDismissedUnreadSignature(unreadCommunicationSignature);
  };

  return (
    <div className={classes.content}>
      <ModalRoot open={showNewCommunicationModal}>
        <Modal showClose={false}>
          <ModalHeader>New Communication</ModalHeader>
          <ModalContent>{NEW_COMMUNICATION_MESSAGE}</ModalContent>
          <ModalFooter>
            <Button color='primary' onClick={handleCloseNewCommunicationModal}>
              OK
            </Button>
          </ModalFooter>
        </Modal>
      </ModalRoot>
      {useRequestsData && (
        <>
          {activeTab === TABS.WORKLIST && (
            <div className={classes.search}>
              <div className={classes.searchInputWrapper}>
                <SearchInput
                  searchValue={searchValue}
                  onChange={setSearchValue}
                  placeholder='Search to filter requests'
                  hideLabel
                />
              </div>
              <button
                type='button'
                className={classes.scanButton}
                aria-label='Scan barcode'
                data-testid='barcode-scan-button'
                onClick={handleBarcodeScan}
              >
                <Icon name='qr_code_2_add' />
              </button>
            </div>
          )}
          {acceptedRequests && <StatusAccordion requests={acceptedRequests} {...REQUESTS.accepted} />}
          {availableRequests && <StatusAccordion requests={availableRequests} {...REQUESTS.available} />}
          {onHoldRequests && <StatusAccordion requests={onHoldRequests} {...REQUESTS.onHold} />}
          <StatusAccordion {...REQUESTS.completed} searchValue={searchValue} />
        </>
      )}
      {activeTab === TABS.QUEUES && (
        <>
          {permanentQueues && <StatusAccordion queues={permanentQueues} {...QUEUES.permanent} />}
          {temporaryQueues && <StatusAccordion queues={temporaryQueues} {...QUEUES.temporary} />}
          {availableQueues && <StatusAccordion queues={availableQueues} {...QUEUES.available} />}
        </>
      )}
    </div>
  );
};
